import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { ModalWithButtons } from '../../ModalWithButtons/ModalWithButtons.component';
import { Grid, TextField, FormHelperTextProps } from '@material-ui/core';
import { ValidationRow } from '../../ValidationRow/ValidationRow.components';
import { LeavePrompt } from '../../Prompt/LeavePrompt/LeavePrompt.component';
import { useStyles } from './PdfEditor.styles';
import { addPdf } from '../../../const/addSupportPdf';
import { useLayoutContext } from '../../../Context/Layout.context';
import { extractFileType, FileType, MAX_FILE_SIZE_25 } from '../../../utils/fileProcessing';
import { Prompt, SubText } from '../../Prompt/Prompt.component';
import Button from '@mui/material/Button';
import PdfPageImage from '../../../assets/images/PdfPageImage.png';

const TITLE_MAX_LENGTH = 256;
const INPUT_TITLE_ID = 'file-input';
const PDF_DOT_EXTENSION = '.pdf';

interface PdfEditorProps {
  title: string;
  onCancel: () => void;
  onSave: (title: string, file: any, pdfId: string) => void;
  onChange: () => void;
  titlePdf: string;
  sizePdf: string;
  pdfId: string;
  file: any;
  path: string;
  fileName: string;
}

export const PdfEditor: React.FC<PdfEditorProps> = ({
  title,
  onCancel,
  onSave,
  titlePdf: titlePdfProp,
  pdfId: pdfIdProp,
  fileName: pdfFileName,
}) => {
  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);
  const [titlePdf, setTitlePdf] = useState<string>(titlePdfProp ?? '');
  const [fileId, setFileId] = useState<string>(pdfIdProp ?? '');
  const [file, setFile] = useState<File>();
  const [hasNameFocus, setHasNameFocus] = useState<boolean>(false);
  const [haveNewFile, setHaveNewFile] = useState<boolean>(false);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);
  const { openAlert } = useLayoutContext();
  const classes = useStyles();

  const [rulesFile, setRulesFile] = useState<File | undefined>();
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isProblemWithFile, setIsProblemWithFile] = useState<boolean>(false);
  const [errorTitle, setErrorTitle] = useState<string>('');
  const [errorSubTexts, setErrorSubTexts] = useState<SubText[]>();

  const hiddenFileInput = useRef<HTMLInputElement>(null);
  
  // Programatically click the hidden file input element
  // when the Button component is clicked
  const handleClick = () => {
    if(hiddenFileInput.current){
      hiddenFileInput.current.click();
    }  
  };

  const handleTitleChange = (value: string) => {
    setTitlePdf(value);

    if (haveNewFile || !title.includes(addPdf)) {
      setHaveNewFile(true);
      setIsDisabled(false);
    }
  };

  const setFileName = (fileName: string) => {
    if (fileName.includes(PDF_DOT_EXTENSION)) {
      setHaveNewFile(true);
      return true;
    } else {
      setHaveNewFile(false);
      return false;
    }
  };

  const handlePdfFileChange = function (e: React.ChangeEvent<HTMLInputElement>) {
    const fileList = e.target.files;

    if (!fileList) return;
    if (setFileName(fileList[0].name)) {
      setFile(fileList[0]);
    } else {
      () => {
        openAlert('error');
      };
    }
  };

  const initialiseInputValue = () => {
    const fileInput = document.getElementById(INPUT_TITLE_ID) as HTMLFormElement;

    const inputValuePhat = pdfFileName;

    let inputValue = inputValuePhat;

    if (inputValuePhat.length > 15) {
      inputValue = '...';
      inputValue += inputValuePhat.slice(inputValuePhat.length - 13);
    }

    const newFile = new File([inputValue], inputValue, {
      type: 'text/plain',
    });

    setFileId('');

    if (fileInput) {
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(newFile);
      fileInput.files = dataTransfer.files;
    }
  };
  const acceptedFileExt = ['pdf'];
  const acceptedFileTypes: FileType[] = ['application/pdf'];

  const handleFilesUploaded = useCallback(
    (files: FileList, event) => {
      files[0].arrayBuffer().then((buffer) => {
        const extractedType = extractFileType(buffer);
        if (files[0].size <= MAX_FILE_SIZE_25) {
          if (files[0].type === extractedType && acceptedFileTypes.includes(extractedType)) {
            setIsProblemWithFile(false);
            setRulesFile(files[0]);
            setIsDisabled(false);
            handlePdfFileChange(event);
          } else {
            setErrorTitle('Nieprawidłowy format pliku');
            setErrorSubTexts([{ text: 'Akceptowany format załączników to: ' }, { text: acceptedFileExt.join(', ') }]);
            setIsProblemWithFile(true);
            setIsDisabled(true);
            console.error(`Format pliku deklarowany: ${files[0].type} odczytany: ${extractedType}`);
            initialiseInputValue();
          }
        } else {
          setErrorTitle('Wybrany plik jest za duży');
          setErrorSubTexts([
            { text: 'Maksymalny rozmiar załączników to: ' },
            { text: `${MAX_FILE_SIZE_25 / 1024 / 1024}MB` },
          ]);
          setIsProblemWithFile(true);
          setIsDisabled(true);
        }
      });
    },
    [rulesFile],
  );


  const handleFileUpload = useCallback(
    (event) => {
      event.stopPropagation();
      event.preventDefault();

      handleFilesUploaded(event.target.files, event);
    },
    [handleFilesUploaded],
  );


  useEffect(() => {
    initialiseInputValue();
  }, [fileId]);

  useEffect(() => {
    setIsDataChanged(titlePdfProp !== titlePdf || haveNewFile);
  }, [titlePdfProp, titlePdf, haveNewFile]);

  const isTitleTooLong = titlePdf.length > TITLE_MAX_LENGTH;

  const handleCancel = () => {
    isDataChanged ? setIsLeavePromptOpen(true) : onCancel();
  };

  return (
    <>
      <ModalWithButtons
        xCloseButton={handleCancel}
        open={true}
        size="xs"
        title={title}
        autoHeight
        onCancel={handleCancel}
        onConfirm={() => onSave(titlePdf, file, pdfIdProp)}
        confirmButtonDisabled={!haveNewFile || !titlePdf || isTitleTooLong || !isDataChanged || isDisabled}
        handleClose={() => setIsLeavePromptOpen(true)}
      >
        <Grid container>
          <Grid item xs={12}>
            <TextField
              value={titlePdf}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleTitleChange(e.target.value)}
              label="Tytuł*"
              variant="outlined"
              fullWidth
              onFocus={() => setHasNameFocus(true)}
              onBlur={() => setHasNameFocus(false)}
              error={isTitleTooLong}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={isTitleTooLong ? 'Podany tytuł jest za długi' : ''}
                  limitsVisible={hasNameFocus || isTitleTooLong}
                  valueLength={titlePdf.length}
                  limit={TITLE_MAX_LENGTH}
                  withError={isTitleTooLong}
                />
              }
            />
          </Grid>
          {isProblemWithFile && (
            <Prompt
              type={'error'}
              open={isProblemWithFile}
              mainText={errorTitle}
              subTexts={errorSubTexts}
              onConfirm={() => setIsProblemWithFile(false)}
            />
          )}
          <div className={classes.pdfEditorChooseFile}>
            <Button variant="contained" onClick={handleClick} style={{ height: '42px', width: '128px' }}>
              <div>Wybierz plik</div>  
            </Button>
              {file && 
              <div className={classes.chosenFileDiv}>
                <img className={classes.helpdeskPicImg} src={PdfPageImage} alt="ikona wybrany plik" />
                <div className={classes.chosenFileNameDiv}>
                  <div className={classes.chosenFileNameDivInside}>{file.name}</div>
                  <div className={classes.chosenFileSizeDiv}>{`${(file.size / 1024 / 1024).toFixed(2)} MB`}</div>
                </div>
              </div>}
            <input
              type="file"
              id={INPUT_TITLE_ID}
              accept="application/pdf"
              ref={hiddenFileInput}
              className={classes.pdfFileInput}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleFileUpload(e)}
              style={{display: 'none'}}
            />
          </div>
        </Grid>
      </ModalWithButtons>
      <LeavePrompt isOpen={isLeavePromptOpen} onConfirm={onCancel} onCancel={() => setIsLeavePromptOpen(false)} />
    </>
  );
};
